import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CheckCircleFilled,
  DeleteOutlined,
  ImportOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";
import { isLocationActive } from "../../utils/helpers/locationVisibility";

const { RangePicker } = DatePicker;

interface TokenOption {
  _id: string;
  name: string;
  locationId?: { _id: string; name: string } | null;
}

interface Invoice {
  InvoiceId: number;
  GlobUniqId: string;
  DocumentNumber: string;
  DocumentType: string;
  Status: string;
  SupplierName: string;
  SupplierRegistrationNumber: string;
  Amount: number;
  SumWithoutVat: number;
  VatAmount: number;
  Currency: string;
  DueDate: string;
  SentDate: string;
}

interface ImportRecord {
  globUniqId: string;
  customValueId?: string;
  categoryId?: string;
  categoryName?: string;
  supplierId?: string;
  supplierName?: string;
}

interface Category {
  _id: string;
  name: string;
  suppliers: { _id: string; name: string }[];
}

interface Location {
  _id: string;
  name: string;
}

const STATUS_COLOR: Record<string, string> = {
  New: "blue",
  Approved: "green",
  Rejected: "red",
  Cancelled: "default",
  Sent: "orange",
  Storno: "volcano",
};

const fmt = (n: number, currency = "RSD") =>
  new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n) +
  " " +
  currency;

const Invoices: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("invoices:view");

  const [tokens, setTokens] = useState<TokenOption[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [invoiceStatus, setInvoiceStatus] = useState<string>("");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // importMap: GlobUniqId → ImportRecord
  const [importMap, setImportMap] = useState<Record<string, ImportRecord>>({});
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Map of custom-value description → info, to visually match eFakture (no DB write)
  type CustomValueMatch = {
    supplierName: string;
    supplierId?: string;
    categoryId?: string;
    categoryName?: string;
  };
  const [customValueDescMap, setCustomValueDescMap] = useState<Map<string, CustomValueMatch>>(new Map());

  // Import modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [importForm] = Form.useForm();
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!clientId || !canView) return;
    setTokensLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/efakture-tokens`)
      .then((res) => {
        setTokens(res.data || []);
        if (res.data?.length === 1) setSelectedTokenId(res.data[0]._id);
      })
      .catch(() => message.error("Greška pri učitavanju tokena"))
      .finally(() => setTokensLoading(false));
  }, [clientId]);

  const filteredInvoices = React.useMemo(() => {
    if (!searchText.trim()) return invoices;
    const normalize = (s: string) =>
      s
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    const q = normalize(searchText);
    return invoices.filter(
      (inv) =>
        normalize(inv.SupplierName).includes(q) ||
        normalize(inv.DocumentNumber).includes(q)
    );
  }, [invoices, searchText]);

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const buildImportMap = (records: ImportRecord[]) =>
    Object.fromEntries(records.map((r) => [r.globUniqId, r]));

  const handleSearch = async () => {
    if (!selectedTokenId) {
      message.warning("Izaberite token");
      return;
    }
    setLoading(true);
    setSearched(true);
    setSelectedRowKeys([]);
    try {
      const dateFrom = dateRange[0].format("YYYY-MM-DD");
      const dateTo = dateRange[1].format("YYYY-MM-DD");
      const invoicesRes = await axios.get(
        `${BASE_API_URL}/${clientId}/efakture-tokens/${selectedTokenId}/invoices`,
        {
          params: {
            dateFrom,
            dateTo,
            ...(invoiceStatus ? { status: invoiceStatus } : {}),
          },
        }
      );
      setInvoices(invoicesRes.data || []);
      // Import status is looked up by GlobUniqId (matching the duplicate-check
      // on the backend), not by date — an invoice can be imported at any time,
      // independent of the date range currently being viewed.
      const globUniqIds = (invoicesRes.data || [])
        .map((inv: Invoice) => inv.GlobUniqId)
        .join(",");

      const [importsRes, customValuesRes, catsRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/${clientId}/efakture-imports`, {
          params: globUniqIds ? { globUniqIds } : {},
        }),
        axios.get(`${BASE_API_URL}/v2/supplier-custom-values`, {
          params: { clientId, dateFrom, dateTo },
        }).catch(() => ({ data: { data: [] } })),
        axios.get(`${BASE_API_URL}/admin/expense-categories`, {
          params: { clientId },
        }).catch(() => ({ data: { categories: [] } })),
      ]);
      setImportMap(buildImportMap(importsRes.data || []));

      const catNameMap: Record<string, string> = Object.fromEntries(
        (catsRes.data?.categories || []).map((c: any) => [c._id, c.name])
      );
      const descMap = new Map<string, CustomValueMatch>();
      for (const cv of customValuesRes.data?.data || []) {
        if (cv.description) {
          descMap.set(cv.description, {
            supplierName: cv.supplierName,
            supplierId: cv.supplierId,
            categoryId: cv.categoryId,
            categoryName: cv.categoryId ? catNameMap[cv.categoryId] : undefined,
          });
        }
      }
      setCustomValueDescMap(descMap);
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Greška pri dohvaćanju faktura"
      );
      setInvoices([]);
      setImportMap({});
      setCustomValueDescMap(new Map());
    } finally {
      setLoading(false);
    }
  };

  const openImportModal = () => {
    importForm.resetFields();
    setSelectedCategoryId(undefined);

    if (!categories.length) {
      setCategoriesLoading(true);
      axios
        .get(`${BASE_API_URL}/admin/expense-categories`, {
          params: { clientId },
        })
        .then((res) => setCategories(res.data?.categories || []))
        .catch(() => message.error("Greška pri učitavanju kategorija"))
        .finally(() => setCategoriesLoading(false));
    }

    if (!locations.length) {
      setLocationsLoading(true);
      axios
        .get(`${BASE_API_URL}/${clientId}/locations`)
        .then((res) => setLocations((res.data || []).filter(isLocationActive)))
        .catch(() => message.error("Greška pri učitavanju lokacija"))
        .finally(() => setLocationsLoading(false));
    }

    setImportModalOpen(true);
  };

  const handleImport = async () => {
    const values = await importForm.validateFields();
    const supplierOption = allSupplierOptions.find(
      (s) => s.value === values.supplierId
    );
    if (!supplierOption) {
      message.error("Dobavljač nije pronađen");
      return;
    }

    const selectedInvoices = invoices.filter((inv) =>
      selectedRowKeys.includes(inv.GlobUniqId)
    );

    setImporting(true);
    try {
      const res = await axios.post(
        `${BASE_API_URL}/${clientId}/efakture-imports`,
        {
          invoices: selectedInvoices,
          categoryId: supplierOption.categoryId,
          categoryName: supplierOption.categoryName,
          supplierId: supplierOption.value,
          supplierName: supplierOption.label,
          locationId: values.locationId,
        }
      );

      const { imported, alreadyImported } = res.data;

      if (imported?.length > 0) {
        message.success(`Uvezeno ${imported.length} faktura`);
        // Refresh import status for the currently-loaded invoices so we get full records back
        const globUniqIds = invoices.map((inv) => inv.GlobUniqId).join(",");
        axios
          .get(`${BASE_API_URL}/${clientId}/efakture-imports`, {
            params: globUniqIds ? { globUniqIds } : {},
          })
          .then((r) => setImportMap(buildImportMap(r.data || [])));
      }
      if (alreadyImported?.length > 0) {
        message.warning(
          `${alreadyImported.length} faktura je već bilo uvezeno`
        );
      }

      setSelectedRowKeys([]);
      setImportModalOpen(false);
    } catch (err: any) {
      const errData = err?.response?.data;
      if (err?.response?.status === 409) {
        message.warning(errData?.message || "Fakture su već uvezene");
      } else {
        message.error(errData?.message || "Greška pri uvozu faktura");
      }
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteImport = async (globUniqId: string) => {
    setDeletingId(globUniqId);
    try {
      await axios.delete(
        `${BASE_API_URL}/${clientId}/efakture-imports/${encodeURIComponent(globUniqId)}`
      );
      message.success("Uvoz obrisan");
      setImportMap((prev) => {
        const next = { ...prev };
        delete next[globUniqId];
        return next;
      });
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || "Greška pri brisanju uvoza"
      );
    } finally {
      setDeletingId(null);
    }
  };

  const allSupplierOptions = categories.flatMap((cat) =>
    cat.suppliers.map((s) => ({
      value: s._id,
      label: s.name,
      categoryId: cat._id,
      categoryName: cat.name,
    }))
  );

  const alreadyImportedSelected = (selectedRowKeys as string[]).filter(
    (k) => importMap[k]
  );

  const buildFinanceUrl = (rec: ImportRecord) => {
    const months: string[] = [];
    let cur = dateRange[0].startOf("month");
    const end = dateRange[1].startOf("month");
    while (cur.isBefore(end) || cur.isSame(end, "month")) {
      months.push(cur.format("YYYY-MM"));
      cur = cur.add(1, "month");
    }
    const params = new URLSearchParams();
    params.set("months", months.join(","));
    if (rec.categoryId) params.set("category", rec.categoryId);
    if (rec.supplierId) params.set("suppliers", String(rec.supplierId));
    return `/finance?${params.toString()}`;
  };

  const columns = [
    {
      title: "Dobavljač",
      dataIndex: "SupplierName",
      key: "SupplierName",
      sorter: (a: Invoice, b: Invoice) =>
        a.SupplierName.localeCompare(b.SupplierName, "sr"),
      render: (name: string, row: Invoice) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>
            {row.SupplierRegistrationNumber}
          </div>
        </div>
      ),
    },
    {
      title: "Broj dokumenta",
      dataIndex: "DocumentNumber",
      key: "DocumentNumber",
      sorter: (a: Invoice, b: Invoice) =>
        a.DocumentNumber.localeCompare(b.DocumentNumber),
      render: (v: string) => <code style={{ fontSize: 12 }}>{v}</code>,
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      sorter: (a: Invoice, b: Invoice) => a.Status.localeCompare(b.Status),
      render: (s: string) => (
        <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag>
      ),
    },
    {
      title: "Iznos bez PDV",
      dataIndex: "SumWithoutVat",
      key: "SumWithoutVat",
      align: "right" as const,
      sorter: (a: Invoice, b: Invoice) => a.SumWithoutVat - b.SumWithoutVat,
      render: (v: number, row: Invoice) => fmt(v, row.Currency),
    },
    {
      title: "PDV",
      dataIndex: "VatAmount",
      key: "VatAmount",
      align: "right" as const,
      sorter: (a: Invoice, b: Invoice) => a.VatAmount - b.VatAmount,
      render: (v: number, row: Invoice) => fmt(v, row.Currency),
    },
    {
      title: "Ukupno",
      dataIndex: "Amount",
      key: "Amount",
      align: "right" as const,
      sorter: (a: Invoice, b: Invoice) => a.Amount - b.Amount,
      render: (v: number, row: Invoice) => (
        <strong>{fmt(v, row.Currency)}</strong>
      ),
    },
    {
      title: "Rok plaćanja",
      dataIndex: "DueDate",
      key: "DueDate",
      sorter: (a: Invoice, b: Invoice) =>
        dayjs(a.DueDate || 0).unix() - dayjs(b.DueDate || 0).unix(),
      render: (d: string) =>
        d ? (
          <Tooltip title={dayjs(d).format("DD.MM.YYYY HH:mm")}>
            <span
              style={{
                color: dayjs(d).isBefore(dayjs()) ? "#ef4444" : undefined,
              }}
            >
              {dayjs(d).format("DD.MM.YYYY")}
            </span>
          </Tooltip>
        ) : (
          "—"
        ),
    },
    {
      title: "Datum slanja",
      dataIndex: "SentDate",
      key: "SentDate",
      sorter: (a: Invoice, b: Invoice) =>
        dayjs(a.SentDate || 0).unix() - dayjs(b.SentDate || 0).unix(),
      render: (d: string) => (d ? dayjs(d).format("DD.MM.YYYY") : "—"),
    },
    {
      title: "",
      key: "importedStatus",
      width: 200,
      render: (_: any, row: Invoice) => {
        const rec = importMap[row.GlobUniqId];
        if (rec) {
          const financeUrl = buildFinanceUrl(rec);
          return (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <CheckCircleFilled
                style={{ color: "#22c55e", fontSize: 15, marginTop: 2, flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {rec.supplierName && (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <a
                      href={financeUrl}
                      onClick={(e) => { e.preventDefault(); navigate(financeUrl); }}
                      style={{ color: "#374151" }}
                    >
                      {rec.supplierName}
                    </a>
                  </div>
                )}
                {rec.categoryName && (
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>
                    {rec.categoryName}
                  </div>
                )}
              </div>
              <Popconfirm
                title="Obriši uvoz"
                description="Faktura i trošak kod dobavljača će biti obrisani."
                okText="Obriši"
                cancelText="Odustani"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDeleteImport(row.GlobUniqId)}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingId === row.GlobUniqId}
                  style={{ flexShrink: 0 }}
                />
              </Popconfirm>
            </div>
          );
        }

        const matchedCv = customValueDescMap.get(row.DocumentNumber);
        if (matchedCv) {
          const financeUrl = buildFinanceUrl({
            globUniqId: "",
            supplierId: matchedCv.supplierId,
            categoryId: matchedCv.categoryId,
            supplierName: matchedCv.supplierName,
            categoryName: matchedCv.categoryName,
          });
          return (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <CheckCircleFilled
                style={{ color: "#22c55e", fontSize: 15, marginTop: 2, flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                {matchedCv.supplierName && (
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <a
                      href={financeUrl}
                      onClick={(e) => { e.preventDefault(); navigate(financeUrl); }}
                      style={{ color: "#374151" }}
                    >
                      {matchedCv.supplierName}
                    </a>
                  </div>
                )}
                {matchedCv.categoryName && (
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>
                    {matchedCv.categoryName}
                  </div>
                )}
              </div>
            </div>
          );
        }

        return null;
      },
    },
  ];

  const total = filteredInvoices.reduce((sum, inv) => sum + (inv.Amount || 0), 0);

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <h1
          style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}
        >
          {t("Invoices")}
        </h1>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals?.[0] && vals?.[1]) setDateRange([vals[0], vals[1]]);
          }}
          format="DD.MM.YYYY"
          style={{ minWidth: 240 }}
        />

        <Select
          value={invoiceStatus}
          onChange={setInvoiceStatus}
          style={{ minWidth: 130 }}
          options={[
            { value: "", label: t("All statuses") },
            { value: "New", label: t("invoice.status.new") },
            { value: "Seen", label: t("invoice.status.seen") },
            { value: "Approved", label: t("invoice.status.approved") },
          ]}
        />

        <Select
          placeholder="Izaberite token"
          value={selectedTokenId}
          onChange={setSelectedTokenId}
          loading={tokensLoading}
          style={{ minWidth: 220 }}
          optionLabelProp="label"
          options={tokens.map((tk) => ({
            value: tk._id,
            label: tk.name,
            desc: tk.locationId?.name,
          }))}
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
              {opt.data.desc && (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>
                  {opt.data.desc}
                </div>
              )}
            </div>
          )}
          notFoundContent={
            tokensLoading
              ? "Učitavanje..."
              : "Nema tokena. Dodajte ih u Podešavanja → eFakture tokeni."
          }
        />

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Pretraži
        </Button>

        {invoices.length > 0 && (
          <Input
            placeholder="Pretraži po dobavljaču ili broju dokumenta..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 280, flex: 1 }}
          />
        )}

        {selectedRowKeys.length > 0 && (
          <Button
            type="primary"
            icon={<ImportOutlined />}
            onClick={openImportModal}
            style={{
              marginLeft: "auto",
              background: "#7c3aed",
              borderColor: "#7c3aed",
            }}
          >
            Uvezi ({selectedRowKeys.length})
          </Button>
        )}
      </div>

      {/* Summary row */}
      {searched && !loading && invoices.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno faktura:{" "}
            <strong style={{ color: "#111827" }}>
              {filteredInvoices.length}
              {filteredInvoices.length !== invoices.length && (
                <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                  {" "}/ {invoices.length}
                </span>
              )}
            </strong>
          </span>
          <span>
            Ukupan iznos:{" "}
            <strong style={{ color: "#111827" }}>
              {fmt(total, invoices[0]?.Currency || "RSD")}
            </strong>
          </span>
        </div>
      )}

      <Table
        dataSource={filteredInvoices}
        columns={columns}
        rowKey="GlobUniqId"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          getCheckboxProps: () => ({}),
        }}
        rowClassName={(row: Invoice) =>
          importMap[row.GlobUniqId] || customValueDescMap.has(row.DocumentNumber)
            ? "efaktura-imported-row"
            : ""
        }
        locale={{
          emptyText: searched
            ? "Nema faktura za izabrani period i token"
            : "Izaberite period i token, pa kliknite Pretraži",
        }}
        scroll={{ x: 900 }}
      />

      {/* Import modal */}
      <Modal
        title="Uvezi fakture u troškove"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleImport}
        okText="Uvezi"
        cancelText="Odustani"
        confirmLoading={importing}
        destroyOnClose
      >
        {alreadyImportedSelected.length > 0 && (
          <Alert
            type="warning"
            style={{ marginBottom: 16 }}
            message={`${alreadyImportedSelected.length} od izabranih faktura je već uvezeno i biće preskočeno`}
          />
        )}

        <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
          Izabrane fakture ({selectedRowKeys.length}) biće dodate kao troškovi
          dobavljača.
        </p>

        <Form form={importForm} layout="vertical">
          <Form.Item
            name="supplierId"
            label="Dobavljač"
            rules={[{ required: true, message: "Izaberite dobavljača" }]}
          >
            <Select
              showSearch
              placeholder="Pretraži dobavljača..."
              loading={categoriesLoading}
              filterOption={(input, opt) =>
                (opt?.label as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()) ||
                (opt?.categoryName as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(val) => {
                const opt = allSupplierOptions.find((s) => s.value === val);
                setSelectedCategoryId(opt?.categoryId);
              }}
              optionLabelProp="label"
              options={allSupplierOptions}
              optionRender={(opt) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{opt.data.label}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>
                    {opt.data.categoryName}
                  </div>
                </div>
              )}
            />
          </Form.Item>

          {selectedCategoryId && (
            <div style={{ marginTop: -12, marginBottom: 16, fontSize: 12, color: "#6b7280" }}>
              Kategorija:{" "}
              <Tag style={{ marginLeft: 4 }}>
                {categories.find((c) => c._id === selectedCategoryId)?.name}
              </Tag>
            </div>
          )}

          <Form.Item
            name="locationId"
            label="Lokacija"
            rules={[{ required: true, message: "Izaberite lokaciju" }]}
          >
            <Select
              placeholder="Izaberite lokaciju"
              loading={locationsLoading}
              options={locations.map((l) => ({ value: l._id, label: l.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .efaktura-imported-row td {
          background: #f0fdf4 !important;
        }
        .efaktura-imported-row:hover td {
          background: #dcfce7 !important;
        }
      `}</style>
    </div>
  );
};

export default Invoices;
